import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import _ from "lodash";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import { GridInputs } from "../../../../../../../components/Functional";
import { Button } from "../../../../../../../components/ui";
import {
  CREATE_FABRICATION as createFabricationMutation,
  UPDATE_FABRICATION as updateFabricationMutation,
} from "../../../../../../../graphql/mutation/Fabrication";
import { FETCH_CURRENCIES } from "../../../../../../../graphql/query/Currency";
import { FETCH_FABRICATION_TYPES } from "../../../../../../../graphql/query/FabricationType";
import {
  currentLocaleISODate,
  getChangedFieldsV2,
  isEqual,
  roundNumber,
} from "../../../../../../../util";

/**
 * This component is used both for editing and creating costs
 * @param {object} props.fabrication - the cost to edit, if any. If not, create
 * @param {number} props.invoiceId
 * @param {function} props.closeModal
 * @param {boolean} props.isOpen
 * @returns {JSX.Element}
 */
const AddCost = props => {
  const { closeModal, fabrication, isOpen, invoiceId } = props;
  const isEditing = Boolean(fabrication?.id);

  const { data: { currencies = [] } = {} } = useQuery(FETCH_CURRENCIES);
  const { data: { fabricationTypes = [] } = {} } = useQuery(
    FETCH_FABRICATION_TYPES,
  );

  const [createFabrication, { loading: loadingCreate }] = useMutation(
    createFabricationMutation({ currency: {}, type: {} }),
    { refetchQueries: ["FetchFabricationsV2"] },
  );

  const [updateFabrication, { loading: loadingUpdate }] = useMutation(
    updateFabricationMutation({ currency: {}, type: {} }),
    { refetchQueries: ["FetchFabricationsV2"] },
  );

  const onSubmit = async () => {
    const input = {
      ...formik.values,
      invoiceId,
    };

    if (isEditing) {
      input.id = fabrication.id;
    }
    const action = isEditing ? updateFabrication : createFabrication;
    const key = isEditing ? "updateFabrication" : "createFabrication";

    const response = await action({ variables: { input } });

    if (response?.data?.[key]?.success) {
      formik.resetForm();
      closeModal();
    }
  };

  useEffect(() => {
    formik.handleReset();
    formik.validateForm();
  }, [isOpen]);

  const [prevValues, setPrevValues] = useState(fabrication);
  const formik = useFormik({
    initialValues: {
      amount: fabrication?.amount,
      currencyId: fabrication?.currency?.id || fabrication?.currencyId,
      date: fabrication?.date || currentLocaleISODate(),
      description: fabrication?.description,
      instanceId: fabrication?.instance?.id || fabrication?.instanceId,
      po: fabrication?.po,
      rechargerate: fabrication?.rechargerate,
      recharge: fabrication?.recharge,
      typeId: fabrication?.type?.id || fabrication?.typeId,
    },
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      date: Yup.date(),
      typeId: Yup.number().required("Required"),
      po: Yup.string()
        .trim()
        .max(20, "PO number must be 20 characters or less")
        .nullable(),
      description: Yup.string().trim().nullable(),
      amount: Yup.number().required("Required"),
      currencyId: Yup.number().required("Required"),
      instanceId: Yup.number().required("Required"),
      recharge: Yup.number().nullable(),
      rechargerate: Yup.number()
        .min(0)
        .max(100)
        .nullable()
        .typeError("Invalid input"),
    }),
    onSubmit,
  });

  useEffect(() => {
    const { amount, recharge, rechargerate } = getChangedFieldsV2(
      formik?.values,
      prevValues,
    );

    if ([amount, recharge, rechargerate].every(el => !_.isFinite(el))) {
      return;
    }

    const newValues = { ...formik.values };

    if (!_.isNil(amount)) {
      if (formik?.values?.rechargerate) {
        const value = roundNumber((formik.values.rechargerate / 100) * amount);

        if (_.isFinite(value)) newValues.recharge = value;
      } else if (formik?.values?.recharge) {
        const value = roundNumber((formik.values.recharge / amount) * 100);

        if (_.isFinite(value)) newValues.rechargerate = value;
      }
    }

    if (!_.isNil(recharge)) {
      if (formik?.values?.amount) {
        const value = roundNumber((recharge / formik.values.amount) * 100);

        if (_.isFinite(value)) newValues.rechargerate = value;
      } else if (formik?.values?.rechargerate) {
        const value = roundNumber(
          recharge / (formik.values.rechargerate / 100),
        );

        if (_.isFinite(value)) newValues.amount = value;
      }
    }

    if (!_.isNil(rechargerate)) {
      if (formik?.values?.amount) {
        const value = roundNumber((rechargerate / 100) * formik.values.amount);

        if (_.isFinite(value)) newValues.recharge = value;
      } else if (formik?.values?.recharge) {
        const value = roundNumber(
          formik.values.recharge / (rechargerate / 100),
        );

        if (_.isFinite(value)) newValues.amount = value;
      }
    }

    if (!isEqual(formik.values, newValues)) {
      formik.setValues(newValues);
      setPrevValues(newValues);
    }
  }, [formik.values]);

  const inputs = {
    className: "grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-12",
    inputs: [
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          { label: "Date", name: "date", type: "date" },
          {
            label: "Type",
            name: "typeId",
            type: "multi-select",
            options: fabricationTypes
              ?.filter(item => item?.category === 2)
              .map(item => ({
                label: item?.description,
                value: item?.id,
              })),
          },
          {
            label: "Purchase Order #",
            name: "po",
            type: "text",
            maxLength: 12,
          },
          {
            label: "Description",
            name: "description",
            rows: 5,
            type: "textarea",
          },
        ],
      },
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          {
            label: "Currency",
            name: "currencyId",
            type: "multi-select",
            options: currencies?.map(currency => ({
              label: `${currency?.description} (${currency?.code})`,
              value: currency?.id,
            })),
          },
          {
            label: "Net Amount",
            name: "amount",
            type: "number-currency",
            emptyToZero: true,
          },
          {
            label: "Recharge Rate",
            name: "rechargerate",
            type: "number-currency",
            emptyToZero: true,
          },
          {
            label: "Recharge Amount",
            name: "recharge",
            type: "number-currency",
            emptyToZero: true,
          },
          {
            label: "Allocation",
            name: "instanceId",
            type: "instanceSearch",
            redirect: true,
            filterInput: [],
          },
        ],
      },
    ],
  };

  const inputProps = {
    ...inputs,
    formik,
  };

  const USER_ACTION = isEditing ? "Edit" : "Add";
  return (
    <div className="flex w-full flex-col p-4 sm:p-8">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          {USER_ACTION} Cost
        </div>
        <div className="flex flex-col">
          <div>
            <Button
              action="default"
              className="mr-8"
              label="Cancel"
              onClick={closeModal}
            />
            <Button
              label={`${USER_ACTION}${loadingUpdate || loadingCreate ? "ing" : ""}`}
              disabled={loadingUpdate || loadingCreate || !formik?.isValid}
              onClick={formik.submitForm}
            />
          </div>
        </div>
      </div>
      <div className="mt-8 pb-8">
        <GridInputs {...inputProps} />
      </div>
    </div>
  );
};

export default AddCost;
